/**
 * Consent Management Component
 * Allows patients to view, grant, and withdraw consents for data processing and treatment
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Notifications as NotificationIcon,
  Description as DocumentIcon,
  DateRange as DateIcon,
  AccessTime as TimeIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  LocalPharmacy as PharmacyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// API Services
import { consentApi } from '../../services/api/patientManagement';

const ConsentManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consents, setConsents] = useState([]);
  const [filteredConsents, setFilteredConsents] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  
  // Form states
  const [consentForm, setConsentForm] = useState({
    consent_type: '',
    consent_text: '',
    expires_at: null,
    terms_accepted: false
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const consentTypes = [
    { 
      value: 'data_processing', 
      label: 'Data Processing', 
      description: 'Allow processing of your personal and medical data for healthcare purposes',
      icon: <ComputerIcon />,
      required: true
    },
    { 
      value: 'treatment', 
      label: 'Medical Treatment', 
      description: 'Consent to receive medical treatment and care from healthcare providers',
      icon: <HospitalIcon />,
      required: true
    },
    { 
      value: 'communication', 
      label: 'Communication', 
      description: 'Allow healthcare providers to contact you via phone, email, or SMS',
      icon: <NotificationIcon />,
      required: false
    },
    { 
      value: 'research', 
      label: 'Research Participation', 
      description: 'Allow your anonymized data to be used for medical research',
      icon: <AssessmentIcon />,
      required: false
    },
    { 
      value: 'marketing', 
      label: 'Marketing Communications', 
      description: 'Receive promotional materials and health-related information',
      icon: <ShareIcon />,
      required: false
    },
    { 
      value: 'emergency', 
      label: 'Emergency Contact', 
      description: 'Allow emergency contacts to be notified in case of medical emergency',
      icon: <WarningIcon />,
      required: false
    }
  ];

  useEffect(() => {
    loadConsents();
  }, []);

  useEffect(() => {
    filterConsents();
  }, [consents, searchTerm, filterStatus, filterType]);

  const loadConsents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await consentApi.getConsents();
      setConsents(response);
    } catch (err) {
      console.error('Error loading consents:', err);
      setError('Failed to load consents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterConsents = () => {
    let filtered = consents;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(consent =>
        consent.consent_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        consent.consent_text.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(consent => consent.status === filterStatus);
    }

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(consent => consent.consent_type === filterType);
    }

    setFilteredConsents(filtered);
  };

  const handleGrantConsent = async () => {
    try {
      setLoading(true);
      await consentApi.grantConsent(consentForm);
      toast.success('Consent granted successfully!');
      setShowGrantDialog(false);
      setConsentForm({
        consent_type: '',
        consent_text: '',
        expires_at: null,
        terms_accepted: false
      });
      await loadConsents();
    } catch (err) {
      console.error('Error granting consent:', err);
      toast.error('Failed to grant consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawConsent = async (consentId) => {
    try {
      setLoading(true);
      await consentApi.withdrawConsent(consentId);
      toast.success('Consent withdrawn successfully!');
      setShowWithdrawDialog(false);
      setSelectedConsent(null);
      await loadConsents();
    } catch (err) {
      console.error('Error withdrawing consent:', err);
      toast.error('Failed to withdraw consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openWithdrawDialog = (consent) => {
    setSelectedConsent(consent);
    setShowWithdrawDialog(true);
  };

  const openDetailsDialog = (consent) => {
    setSelectedConsent(consent);
    setShowDetailsDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'granted': return 'success';
      case 'withdrawn': return 'error';
      case 'expired': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'granted': return <CheckIcon />;
      case 'withdrawn': return <CancelIcon />;
      case 'expired': return <WarningIcon />;
      case 'pending': return <TimeIcon />;
      default: return <InfoIcon />;
    }
  };

  const getTypeIcon = (type) => {
    const consentType = consentTypes.find(ct => ct.value === type);
    return consentType ? consentType.icon : <SecurityIcon />;
  };

  const getTypeLabel = (type) => {
    const consentType = consentTypes.find(ct => ct.value === type);
    return consentType ? consentType.label : type;
  };

  const getTypeDescription = (type) => {
    const consentType = consentTypes.find(ct => ct.value === type);
    return consentType ? consentType.description : '';
  };

  const isConsentExpired = (consent) => {
    if (!consent.expires_at) return false;
    return new Date(consent.expires_at) < new Date();
  };

  const isConsentValid = (consent) => {
    return consent.status === 'granted' && !isConsentExpired(consent);
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ConsentCard = ({ consent, onView, onWithdraw }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              {getTypeIcon(consent.consent_type)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div">
                {getTypeLabel(consent.consent_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTypeDescription(consent.consent_type)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={consent.status}
              color={getStatusColor(consent.status)}
              size="small"
              icon={getStatusIcon(consent.status)}
            />
            {isConsentExpired(consent) && (
              <Chip label="Expired" size="small" color="warning" />
            )}
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Granted:</strong> {consent.granted_at ? format(new Date(consent.granted_at), 'MMM d, yyyy') : 'Not granted'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Expires:</strong> {consent.expires_at ? format(new Date(consent.expires_at), 'MMM d, yyyy') : 'Never'}
            </Typography>
          </Grid>
          {consent.withdrawn_at && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Withdrawn:</strong> {format(new Date(consent.withdrawn_at), 'MMM d, yyyy')}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions>
        <Button size="small" startIcon={<ViewIcon />} onClick={() => onView(consent)}>
          View Details
        </Button>
        {isConsentValid(consent) && (
          <Button size="small" startIcon={<CancelIcon />} color="error" onClick={() => onWithdraw(consent)}>
            Withdraw
          </Button>
        )}
      </CardActions>
    </Card>
  );

  if (loading && consents.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Consent Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowGrantDialog(true)}
        >
          Grant New Consent
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Consents"
            value={consents.length}
            icon={<SecurityIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Consents"
            value={consents.filter(c => isConsentValid(c)).length}
            icon={<CheckIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Withdrawn"
            value={consents.filter(c => c.status === 'withdrawn').length}
            icon={<CancelIcon />}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expired"
            value={consents.filter(c => isConsentExpired(c)).length}
            icon={<WarningIcon />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search consents"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="granted">Granted</MenuItem>
                <MenuItem value="withdrawn">Withdrawn</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {consentTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredConsents.length} of {consents.length} consents
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Consents List */}
      {filteredConsents.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No consents found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || filterStatus !== 'all' || filterType !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Start by granting your first consent.'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowGrantDialog(true)}
          >
            Grant First Consent
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredConsents
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((consent) => (
              <ConsentCard
                key={consent.id}
                consent={consent}
                onView={openDetailsDialog}
                onWithdraw={openWithdrawDialog}
              />
            ))}
        </Stack>
      )}

      {/* Pagination */}
      {filteredConsents.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={filteredConsents.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}

      {/* Grant Consent Dialog */}
      <Dialog open={showGrantDialog} onClose={() => setShowGrantDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Grant New Consent</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Consent Type</InputLabel>
                <Select
                  value={consentForm.consent_type}
                  onChange={(e) => setConsentForm({ ...consentForm, consent_type: e.target.value })}
                  label="Consent Type"
                >
                  {consentTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {type.icon}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body1">{type.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {type.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Consent Text"
                value={consentForm.consent_text}
                onChange={(e) => setConsentForm({ ...consentForm, consent_text: e.target.value })}
                placeholder="Enter the specific consent text..."
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiration Date (Optional)"
                  value={consentForm.expires_at}
                  onChange={(date) => setConsentForm({ ...consentForm, expires_at: date })}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentForm.terms_accepted}
                    onChange={(e) => setConsentForm({ ...consentForm, terms_accepted: e.target.checked })}
                  />
                }
                label="I understand and agree to the terms of this consent"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGrantDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleGrantConsent} 
            variant="contained" 
            disabled={loading || !consentForm.consent_type || !consentForm.consent_text || !consentForm.terms_accepted}
          >
            {loading ? <CircularProgress size={20} /> : 'Grant Consent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consent Details Dialog */}
      <Dialog open={showDetailsDialog} onClose={() => setShowDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Consent Details</DialogTitle>
        <DialogContent>
          {selectedConsent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {getTypeLabel(selectedConsent.consent_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getTypeDescription(selectedConsent.consent_type)}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong> {selectedConsent.status}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Version:</strong> {selectedConsent.version}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Granted:</strong> {selectedConsent.granted_at ? format(new Date(selectedConsent.granted_at), 'MMM d, yyyy HH:mm') : 'Not granted'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Expires:</strong> {selectedConsent.expires_at ? format(new Date(selectedConsent.expires_at), 'MMM d, yyyy HH:mm') : 'Never'}
                  </Typography>
                </Grid>
                {selectedConsent.withdrawn_at && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Withdrawn:</strong> {format(new Date(selectedConsent.withdrawn_at), 'MMM d, yyyy HH:mm')}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>IP Address:</strong> {selectedConsent.ip_address || 'Not recorded'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>User Agent:</strong> {selectedConsent.user_agent || 'Not recorded'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Consent Text:</strong>
                  </Typography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {selectedConsent.consent_text}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Consent Dialog */}
      <Dialog open={showWithdrawDialog} onClose={() => setShowWithdrawDialog(false)}>
        <DialogTitle>Withdraw Consent</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to withdraw this consent?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Type:</strong> {selectedConsent && getTypeLabel(selectedConsent.consent_type)}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Withdrawing this consent may affect your ability to receive certain healthcare services.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWithdrawDialog(false)}>Cancel</Button>
          <Button onClick={() => selectedConsent && handleWithdrawConsent(selectedConsent.id)} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Withdraw Consent'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsentManagement;
